import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Messaging, getToken, onMessage } from 'firebase/messaging';
import { FIREBASE_MESSAGING } from '../firebase-config';
import { GlobalService } from './global.service';
import { UserService } from './user.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  private platform = inject(Platform);
  private globalService = inject(GlobalService);
  private userService = inject(UserService);
  private messaging = inject(FIREBASE_MESSAGING, { optional: true });
  private nativeListenersBound = false;
  private webForegroundListenerBound = false;
  private webServiceWorkerMessageBound = false;

  private isWebPushDebugEnabled(): boolean {
    try {
      return !environment.production && typeof window !== 'undefined' && window.localStorage?.getItem('bebop_push_debug') === '1';
    } catch {
      return false;
    }
  }

  private debugLog(...args: unknown[]): void {
    if (this.isWebPushDebugEnabled()) {
      console.log('[BebopPush]', ...args);
    }
  }

  /**
   * Initialize push notifications with fallback support:
   * - On native platforms (iOS/Android): Uses Capacitor PushNotifications
   * - On web/PWA: Uses Firebase Cloud Messaging with Web Push API
   * 
   * Note: If notification_id is already set in user profile (set during phone verification),
   * this will still set up listeners but won't fetch a new token.
   */
  async initializeNotifications(): Promise<void> {
    await this.platform.ready();

    if (Capacitor.isNativePlatform()) {
      this.setupCapacitorNotifications();
    } else {
      this.setupWebNotifications();
    }
  }

  /**
   * Bind the native tap/receive listeners as early as possible, without requesting
   * permissions or registering for a token. A cold start launched by tapping a
   * notification replays that queued native event to the first listener bound,
   * so this must not wait on auth/profile loading or the event can arrive after
   * app-level default navigation has already run.
   */
  async bindNativeListenersEarly(): Promise<void> {
    await this.platform.ready();

    if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('PushNotifications')) {
      return;
    }

    this.bindNativeListeners();
  }

  /**
   * Setup Capacitor push notifications for native platforms
   */
  private setupCapacitorNotifications(): void {
    if (!Capacitor.isPluginAvailable('PushNotifications')) {
      console.warn('PushNotifications plugin not available');
      return;
    }

    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        PushNotifications.register().catch(e => {
          console.error('Capacitor push registration error:', e);
        });
      } else {
        console.warn('Push notification permissions denied');
      }
    });

    this.bindNativeListeners();
  }

  private bindNativeListeners(): void {
    if (this.nativeListenersBound) {
      return;
    }
    this.nativeListenersBound = true;

    // Listener for successful token registration
    PushNotifications.addListener('registration', (token: Token) => {
      // console.log('Capacitor push registration success, token:', token.value);
      this.userService.saveNotificationToken(token.value);
    });

    // Listener for registration errors
    PushNotifications.addListener('registrationError', (error: unknown) => {
      console.error('Capacitor registration error:', error);
    });

    // Listener for foreground notifications
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      // console.log('Push notification received (foreground):', notification);
      this.globalService.handlePushNotification(notification, true);
    });

    // Listener for notification tap
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      // console.log('Push notification action performed:', notification);
      this.globalService.handlePushNotification(notification.notification, false);
    });
  }


  /**
   * Setup Web Push notifications using Firebase Cloud Messaging
   */
  private async setupWebNotifications(): Promise<void> {
    try {
      // Skip service worker flow on native WebView (no SW support)
      if (Capacitor.isNativePlatform()) {
        return;
      }

      this.debugLog('Init web push', {
        href: typeof window !== 'undefined' ? window.location.href : undefined,
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'Notification API missing'
      });

      const messaging = this.messaging;
      if (!messaging) {
        console.warn('Firebase Messaging not available; skipping web notifications');
        return;
      }

      // Request notification permission from the user
      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        this.debugLog('Notification.requestPermission result:', permission);
        if (permission !== 'granted') {
          if (!environment.production) {
            console.warn('Browser notification permission denied by user');
          }
          return;
        } else {
          if (!environment.production) {
            // console.log('Browser notification permission granted');
          }
        }
      }

      // Check if notifications are supported but permission was previously denied
      if ('Notification' in window && Notification.permission === 'denied') {
        if (!environment.production) {
          console.warn('Browser notifications are blocked. Please enable them in browser settings.');
        }
        return;
      } else {
        if (!environment.production) {
          // console.log('Browser notifications are supported and permission is granted');
        }
      }

      // Register Firebase Messaging service worker
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          this.debugLog('SW registered', {
            scope: registration.scope,
            active: registration.active?.scriptURL,
            installing: registration.installing?.scriptURL,
            waiting: registration.waiting?.scriptURL
          });
          if (!environment.production) {
            // console.log('✅ Firebase messaging service worker registered');
          }
          // Listen for messages forwarded by the service worker for easier debugging
          if (!environment.production && !this.webServiceWorkerMessageBound) {
            this.webServiceWorkerMessageBound = true;
            navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
              this.debugLog('Message from SW:', event.data);
            });
          }
        } catch (error) {
          console.warn('⚠️ Service worker registration failed:', error);
          return; // Can't proceed without service worker
        }
      } else {
        console.warn('Service workers not supported in this browser');
        return;
      }

      // Get FCM token with service worker registration
      const registration = await this.getServiceWorkerRegistration();

      this.debugLog('SW ready registration', {
        scope: registration?.scope,
        active: registration?.active?.scriptURL
      });
      
      try {
        if (!environment.production) {
          // console.log('🔑 Requesting FCM token with VAPID key...');
        }
        
        // Diagnostic: Check push subscription
        if (!environment.production) {
          if (registration && registration.pushManager) {
            const subscription = await registration.pushManager.getSubscription();
            this.debugLog('Push subscription exists before token:', !!subscription);
            if (subscription) {
              try {
                // Subscription details help diagnose wrong-origin / wrong-project issues.
                this.debugLog('Push subscription details (endpoint):', subscription.endpoint);
                // `toJSON()` is widely supported and safely serializes keys.
                this.debugLog('Push subscription details (json):', subscription.toJSON());
              } catch {
                // ignore
              }
            }
            if (!subscription) {
              // console.warn('⚠️ No push subscription found. Push notifications may not work on localhost.');
            }
          }
        }
        
        this.debugLog('Requesting FCM token...');
        const token = await getToken(messaging, {
          vapidKey: this.getVapidKey(),
          serviceWorkerRegistration: registration
        });

        this.debugLog('FCM getToken result:', token ? `${token.slice(0, 16)}…` : '(empty)');
        if (token && this.isWebPushDebugEnabled()) {
          // Helpful when copy/pasting into curl; keep local-only.
          this.debugLog('FCM token (full):', token);
        }

        if (token) {
          if (!environment.production) {
            // console.log('✅ FCM token obtained - notifications enabled');
            // console.log('📱 Token:', token);
          }
          await this.userService.saveNotificationToken(token);

          // Ensure a push subscription exists after obtaining the token (critical for delivery)
          if (registration && registration.pushManager) {
            let subAfter = await registration.pushManager.getSubscription();
            if (!environment.production) {
              this.debugLog('Push subscription exists after token:', !!subAfter);
            }
            if (!subAfter) {
              try {
                if (!environment.production) {
                  // console.log('🧩 Creating push subscription with VAPID applicationServerKey...');
                }
                subAfter = await registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: this.vapidKeyToArrayBuffer(this.getVapidKey())
                });
                if (!environment.production) {
                  this.debugLog('Push subscription created:', !!subAfter);
                }
              } catch (subErr: unknown) {
                console.error('❌ Push subscription creation failed:', this.extractErrorMessage(subErr));
              }
            }
          }
        } else {
          throw new Error('No token returned');
        }
      } catch (tokenError: unknown) {
        const tokenErrorCode = this.readStringField(tokenError, 'code') || '';
        const tokenErrorMessage = this.extractErrorMessage(tokenError);
        console.error('⚠️ Could not obtain FCM token:', tokenErrorCode || tokenErrorMessage);
        this.debugLog('getToken error details:', tokenError);
        throw tokenError; // Re-throw to be caught by outer catch
      }

      // Listen for messages when app is in foreground
      if (!this.webForegroundListenerBound) {
        this.webForegroundListenerBound = true;
        onMessage(messaging, (payload) => {
          this.debugLog('onMessage (foreground) payload:', payload);
          // console.log('Firebase message received (foreground):', payload);
          
          // Convert FCM payload to format expected by handlePushNotification
          const fgTitle = payload.notification?.title || '';
          const fgBody = payload.notification?.body || '';
          const initialData: Record<string, string> = {};
          const data: Record<string, string> = payload.data
            ? Object.entries(payload.data).reduce((accumulator, [key, value]) => {
                accumulator[key] = value ?? '';
                return accumulator;
              }, initialData)
            : {};
          // Ensure title/body are present in data for robust handling downstream
          if (!data['title'] && fgTitle) {
            data['title'] = fgTitle;
          }
          if (!data['body'] && fgBody) {
            data['body'] = fgBody;
          }
          const notification = {
            title: fgTitle,
            body: fgBody,
            data,
            image: payload.notification?.image
          };

          this.globalService.handlePushNotification(notification, true);

          // Also show browser notification if app is in foreground
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.body,
              icon: notification.image || '/assets/icons/icon-72x72.png',
              badge: '/assets/icons/icon-72x72.png',
              tag: 'bebop-notification',
              data: notification.data
            });
          } else {
            this.debugLog('Not showing Notification(): permission is', ('Notification' in window ? Notification.permission : 'missing'));
          }
        });
      }

    } catch (error: unknown) {
      // console.error('❌ Web push notification setup failed:', error.code || error.message);
      this.debugLog('Web push setup failed:', error);
      if (!environment.production) {
        // console.log('💡 Notifications will still work on native iOS/Android apps');
      }
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    const message = this.readStringField(error, 'message');
    if (message) {
      return message;
    }

    return String(error);
  }

  private readStringField(source: unknown, field: string): string | undefined {
    if (typeof source !== 'object' || source === null) {
      return undefined;
    }

    const value = Reflect.get(source, field);
    return typeof value === 'string' ? value : undefined;
  }

  /**
   * Get service worker registration (helper for development mode)
   */
  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
    if ('serviceWorker' in navigator) {
      try {
        return await navigator.serviceWorker.ready;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  /**
   * Get VAPID key from environment config
   */
  private getVapidKey(): string {
    const vapidKey = environment.firebaseConfig.vapid || '';
    if (!vapidKey) {
      // console.warn('VAPID key not configured in environment');
    } else {
      if (!environment.production) {
        // console.log('Using VAPID key for FCM:', vapidKey);
      }
    }
    return vapidKey;
  }

  /**
   * Convert a Base64 URL-safe VAPID public key to ArrayBuffer for PushManager
   */
  private vapidKeyToArrayBuffer(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
  }

  /**
   * Request notification permission (for web)
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  /**
   * Get notification token (FCM or native) without full initialization.
   * This can be called early in the registration flow (e.g., after phone verification).
   */
  async getNotificationTokenAsync(): Promise<string | null> {
    await this.platform.ready();

    if (Capacitor.isNativePlatform()) {
      return this.getNativeNotificationToken();
    } else {
      return this.getWebNotificationToken();
    }
  }

  /**
   * Get FCM token for native platforms via Capacitor
   */
  private async getNativeNotificationToken(): Promise<string | null> {
    if (!Capacitor.isPluginAvailable('PushNotifications')) {
      console.warn('PushNotifications plugin not available');
      return null;
    }

    try {
      // Request permissions
      const result = await PushNotifications.requestPermissions();
      if (result.receive !== 'granted') {
        console.warn('Push notification permissions denied');
        return null;
      }

      // Register and get token via Promise
      return new Promise((resolve, reject) => {
        const tokenHandler = (token: Token) => {
          resolve(token.value);
          // Remove listener after getting token
          PushNotifications.removeAllListeners();
        };

        const errorHandler = (error: unknown) => {
          reject(error);
          // Remove listener on error
          PushNotifications.removeAllListeners();
        };

        // Add one-time listeners
        PushNotifications.addListener('registration', tokenHandler);
        PushNotifications.addListener('registrationError', errorHandler);

        // Trigger registration
        PushNotifications.register().catch(reject);
      });
    } catch (error: unknown) {
      console.error('Failed to get native notification token:', error);
      return null;
    }
  }

  /**
   * Get FCM token for web platform
   */
  private async getWebNotificationToken(): Promise<string | null> {
    try {
      // Skip on native WebView
      if (Capacitor.isNativePlatform()) {
        return null;
      }

      const messaging = this.messaging;
      if (!messaging) {
        console.warn('Firebase Messaging not available');
        return null;
      }

      // Request permission if needed
      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.warn('Browser notification permission denied');
          return null;
        }
      }

      // Skip if permission was previously denied
      if ('Notification' in window && Notification.permission === 'denied') {
        console.warn('Browser notifications are blocked');
        return null;
      }

      // Register service worker
      let registration: ServiceWorkerRegistration | undefined;
      if ('serviceWorker' in navigator) {
        try {
          registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          this.debugLog('SW registered for token fetch', { scope: registration.scope });
        } catch (error) {
          console.warn('Service worker registration failed for token fetch:', error);
          return null;
        }
      } else {
        console.warn('Service workers not supported');
        return null;
      }

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: this.getVapidKey(),
        serviceWorkerRegistration: registration
      });

      this.debugLog('FCM token obtained during registration:', token ? `${token.slice(0, 16)}…` : '(empty)');
      return token || null;
    } catch (error: unknown) {
      console.error('Failed to get web notification token:', error);
      return null;
    }
  }
}
